import { useCallback, useEffect, useReducer, type MouseEvent } from 'react'
import { PhoneOutgoing, ScrollText } from 'lucide-react'
import type { AudioNote, CalendarEvent } from '@highlight/common'
import { isDayToday, isWithinNextMinutes } from '@shared/utils/DateUtils'
import { isNoteForCalendarEvent } from '@shared/utils/audio-notes/AudioNoteUtils'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { useViewActions, type ViewOpenTarget } from '../../view-atoms'
import type { CellAction } from '../cell-actions'
import { MEETING_VIEW_ID } from './meeting-view-id'

/**
 * The meeting quick actions — the Join/View Summary gates and the moves they
 * feed (Figma 8425:91176). `MeetingCell` and the flat row's actions both
 * consume these, so the rules have one home and the two hosts can't drift.
 */

/** Join is offered from 10 minutes before start until the meeting ends. */
export function isJoinWindowOpen(meeting: CalendarEvent): boolean {
  // Both forms, deliberately: `isWithinNextMinutes(start, 10)` only matches a
  // start still in the future, while the 1-minute form is the one that spans
  // an ongoing meeting until its end.
  return isWithinNextMinutes(meeting.start, 10, meeting.end) || isWithinNextMinutes(meeting.start, 1, meeting.end)
}

const JOIN_WINDOW_LEAD_MS = 10 * 60 * 1000

/**
 * `isJoinWindowOpen`, re-rendering as the window opens and closes — and at the
 * start, which leaves the window open but turns the CTAs it gates prominent
 * (`isHappeningNow`). Without that wake-up a row sits on its times across the
 * start until some unrelated store update happens to re-render it.
 */
export function useIsJoinWindowOpen(meeting: CalendarEvent): boolean {
  const isOpen = isJoinWindowOpen(meeting)
  const [tick, rerender] = useReducer((count: number) => count + 1, 0)
  const startMs = new Date(meeting.start).getTime()
  const endMs = meeting.end ? new Date(meeting.end).getTime() : undefined

  // Keyed on `tick` rather than `isOpen`: the start wake-up leaves `isOpen`
  // true, and only a changed dep reschedules the one for the end.
  useEffect(() => {
    const now = Date.now()
    const nextChange = [startMs - JOIN_WINDOW_LEAD_MS, startMs, endMs].find((at) => at !== undefined && at > now)
    if (nextChange === undefined) return
    const timeout = setTimeout(rerender, nextChange - now + 1)
    return () => clearTimeout(timeout)
  }, [startMs, endMs, tick])

  return isOpen
}

/**
 * A linked note is a "View Summary" candidate only when it ended today and is
 * in the past — not a live/in-progress recording or a stale stub.
 */
export function isSummaryReadyNote(note: AudioNote): boolean {
  const ended = new Date(note.endedAt)
  return ended.getTime() <= Date.now() && isDayToday(ended)
}

/** The linked note View Summary would open, if the meeting has one. */
export function findSummaryReadyNote(notes: Array<AudioNote>, meeting: CalendarEvent): AudioNote | undefined {
  const note = notes.find((n) => isNoteForCalendarEvent(n, meeting))
  return note && isSummaryReadyNote(note) ? note : undefined
}

/**
 * Opens a meeting note on its Summary tab, with the shared tracking event.
 * `trigger` is per call — the cell fires this from its footer/keyboard path
 * and its hover cluster under different triggers.
 */
export function useOpenMeetingSummary(
  meeting: CalendarEvent,
): (audioNoteId: string, trigger: string, open?: ViewOpenTarget) => void {
  const { openView } = useViewActions()
  return useCallback(
    (audioNoteId: string, trigger: string, open?: ViewOpenTarget) => {
      openView(
        MEETING_VIEW_ID,
        { mode: 'note', audioNoteId, title: meeting.title, initialTab: 'recap' },
        { sourceLabel: 'Home', ...open },
      )
      AmplitudeRenderer.trackEvent('Audio Note Action', { action: 'view summary', trigger })
    },
    [meeting.title, openView],
  )
}

interface MeetingCellMoves {
  showSummary: boolean
  onViewSummary: (event: MouseEvent) => void
  /**
   * Join as one of the moves, for a host with nowhere else to put it: the flat
   * row. Home's lead card leaves it off and renders the full Join CTA in its
   * footer (Figma `6972:11459`), so it never shows two Joins at once.
   */
  showJoin?: boolean
  joinLabel?: string
  isAwaitingJoin?: boolean
  onJoin?: (event: MouseEvent) => void
}

/** What a meeting offers, for both its hover cluster and its right-click menu. */
export function meetingCellActions({
  showSummary,
  onViewSummary,
  showJoin = false,
  joinLabel,
  isAwaitingJoin,
  onJoin,
}: MeetingCellMoves): Array<CellAction> {
  return [
    ...(showSummary
      ? [
          {
            key: 'summary',
            label: 'View Summary',
            icon: <ScrollText />,
            onSelect: onViewSummary,
          } satisfies CellAction,
        ]
      : []),
    ...(showJoin && joinLabel && onJoin
      ? [
          {
            key: 'join',
            label: joinLabel,
            icon: <PhoneOutgoing />,
            tone: 'meeting',
            disabled: isAwaitingJoin,
            onSelect: onJoin,
          } satisfies CellAction,
        ]
      : []),
  ]
}
